// Utilidades para evitar la "pantalla en blanco" por archivos viejos en caché
// tras un nuevo despliegue. Pensado para usuarios no técnicos (niños): si algo
// queda pegado, basta con tocar un botón grande y la app se limpia y recarga.
//
// IMPORTANTE: la auto-recuperación tiene un TOPE de intentos para no caer en un
// bucle infinito de recargas. Si tras varios intentos el error persiste, se
// detiene y se muestra la pantalla de recuperación manual (botón grande).

const RECOVERY_KEY = "__jasq_recovery";
const MAX_ATTEMPTS = 4; // recargas automáticas máximas dentro de la ventana
const WINDOW_MS = 60000; // ventana de conteo
const HEALTHY_AFTER_MS = 30000; // tras este tiempo sin fallos, la app es "sana"

function readRecovery() {
  try { return JSON.parse(sessionStorage.getItem(RECOVERY_KEY) || "null"); } catch (_) { return null; }
}
function writeRecovery(v) {
  try { sessionStorage.setItem(RECOVERY_KEY, JSON.stringify(v)); } catch (_) {}
}

// Borra Service Workers y Cache Storage del navegador. Nunca lanza error.
export async function clearAppCaches() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (_) {}
  try {
    if (typeof caches !== "undefined" && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (_) {}
}

function reloadWithBuster() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_r", Date.now().toString());
    window.location.replace(url.toString());
    return;
  } catch (_) {}
  window.location.reload();
}

// Recarga forzada (acción MANUAL del usuario): siempre limpia y recarga, sin
// tope, y reinicia el contador de intentos.
export async function clearCachesAndReload() {
  try { sessionStorage.removeItem(RECOVERY_KEY); } catch (_) {}
  await clearAppCaches();
  reloadWithBuster();
}

// Auto-recuperación con tope de intentos para NO caer en bucle. Devuelve true si
// disparó la recarga, false si ya se agotaron los intentos (el caller debe
// mostrar la pantalla de recuperación manual y NO recargar de nuevo).
export async function attemptAutoRecover() {
  // En navegadores incompatibles no reintentamos: ya se mostró el aviso de
  // "actualiza tu navegador"; recargar solo causaría un bucle.
  if (typeof window !== "undefined" && window.__JASQ_UNSUPPORTED__) return false;
  const now = Date.now();
  const rec = readRecovery();
  if (rec && now - rec.firstAt < WINDOW_MS) {
    if (rec.count >= MAX_ATTEMPTS) return false; // demasiados intentos → parar
    writeRecovery({ firstAt: rec.firstAt, count: rec.count + 1 });
  } else {
    writeRecovery({ firstAt: now, count: 1 });
  }
  await clearAppCaches();
  reloadWithBuster();
  return true;
}

// Marca la app como "sana": limpia el contador de recuperación. Se llama cuando
// la app lleva un rato corriendo sin fallos de chunk.
export function markAppHealthy() {
  try { sessionStorage.removeItem(RECOVERY_KEY); } catch (_) {}
}

// Detecta el error típico de "chunk" (archivo JS viejo que ya no existe tras un
// deploy) y se auto-recupera con tope de intentos.
export function installChunkErrorRecovery() {
  const isChunkError = (msg = "") =>
    /ChunkLoadError|Loading chunk\s+[\d]+\s+failed|Loading chunk|Loading CSS chunk|Importing a module script failed|module script failed|dynamically imported( module)?|failed to fetch dynamically imported/i.test(
      String(msg)
    );

  const onErr = (msg) => {
    if (isChunkError(msg)) { attemptAutoRecover(); return true; }
    return false;
  };

  window.addEventListener("error", (e) => {
    if (!onErr(e?.message)) onErr(e?.error?.message);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    if (!onErr(reason?.message)) onErr(reason);
  });

  // Si la app sigue viva tras HEALTHY_AFTER_MS, asumimos que cargó bien y
  // reseteamos el contador (un futuro deploy podrá recuperarse de nuevo).
  setTimeout(markAppHealthy, HEALTHY_AFTER_MS);
}
